/* eslint-disable jsx-a11y/control-has-associated-label */
/** @jsxImportSource @emotion/react */

import { AlertTriangleFilled, CheckCircleFilled } from "@imwebme/clay-icons";
import type { CSSProperties, ChangeEvent, ChangeEventHandler, ComponentProps, Ref } from "react";
import { forwardRef, useMemo, useState } from "react";
import { Typography } from "../Typography";
import { Flex } from "../utilities";
import { TextfieldPasswordIcon } from "./Textfield.addon";
import { inputCSS } from "./Textfield.css";
import type { TextfieldInputProps } from "./Textfield.types";

export const TextfieldInput = forwardRef(
  (
    {
      value,
      onChange,
      onClick,
      onChangeText,
      align,
      maxLength,
      isValid,
      type,
      ...restProps
    }: TextfieldInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [passwordShow, setPasswordShow] = useState(false);

    const _onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const newValue = event.target.value;

      if (maxLength && newValue.length > maxLength) {
        return;
      }

      onChange?.(event);
      onChangeText?.(newValue);
    };

    const cssVariables = {
      "--text-align": align,
    } as CSSProperties;

    return (
      <>
        <input
          ref={ref}
          type={type === "password" && passwordShow ? "text" : type}
          value={value}
          aria-invalid={typeof isValid === "boolean" ? !isValid : undefined}
          onClick={onClick}
          onChange={_onChange}
          maxLength={maxLength}
          style={cssVariables}
          css={inputCSS}
          {...restProps}
        />

        {isValid === undefined && maxLength && (
          <Typography colorToken="textSub">
            {value?.toString().length}/{maxLength}
          </Typography>
        )}

        {type === "password" && (
          <TextfieldPasswordIcon setPasswordShow={setPasswordShow} disabled={restProps.disabled} />
        )}

        {typeof isValid === "boolean" && (
          <Flex
            className="clay-textfield-valid-icon-container"
            width="24px"
            justifyContent="flex-end"
            sx={{
              "input[aria-invalid]:focus ~ &": {
                display: "none",
              },
            }}
          >
            {isValid ? (
              <CheckCircleFilled colorToken="icon-success" />
            ) : (
              <AlertTriangleFilled colorToken="icon-critical" />
            )}
          </Flex>
        )}
      </>
    );
  },
);

export const TextfieldNumericInput = ({
  onChange,
  value,
  numberFormatOption,
  onChangeText,
  locales = "ko-KR",
  ...props
}: ComponentProps<typeof TextfieldInput> & {
  locales?: Intl.LocalesArgument;
  numberFormatOption?: Intl.NumberFormatOptions;
}) => {
  const formatWithLocalString = useMemo(
    () => (value ? Number(value).toLocaleString(locales, numberFormatOption) : undefined),
    [locales, numberFormatOption, value],
  );

  return (
    <TextfieldInput
      onInput={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      value={formatWithLocalString}
      onChange={(e) => {
        const onlyNumber = e.target.value.replace(/\D/g, "");

        onChange?.({
          target: {
            value: onlyNumber,
          } as Pick<HTMLInputElement, "value">,
        } as ChangeEvent<HTMLInputElement>);

        onChangeText?.(onlyNumber);
      }}
      onKeyDownCapture={(e) => {
        if (e.key === "e") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData("text");
        const deletedNotNumber = paste.replace(/\D/g, "");

        onChange?.({
          target: {
            value: deletedNotNumber,
          } as Pick<HTMLInputElement, "value">,
        } as ChangeEvent<HTMLInputElement>);

        onChangeText?.(deletedNotNumber);
      }}
      align="right"
      {...props}
    />
  );
};
